import gql from 'graphql-tag';
import {useMemo, useRef} from 'react';

import * as Generated from '../../generated/graphql';
import {useCachedData} from '../../util/cache';
import * as FilterTypes from '../../util/filterTypes';
import * as RunTypes from '../../util/runTypes';
import {RunSetConfig} from '../../util/section';
import * as SM from '../../util/selectionmanager';
import {useRunsQueryContext} from '../runs/hooks';
import * as CustomRunNamesTypes from '../views/customRunNames/types';
import * as ViewHooks from '../views/hooks';
import {useRunSetProject} from '../views/runSet/hooks';
import * as RunSetTypes from '../views/runSet/types';
import * as TempSelectionsTypes from '../views/tempSelections/types';
import {combinedFilter} from './runSelectorQuery';

///// Query definition
export const SELECTED_COUNT_QUERY = gql`
  query SelectedCount(
    $projectInternalId: ID!
    $filters: JSONString
    $tempFilters: JSONString
  ) {
    project(internalId: $projectInternalId) {
      id
      selectedCount: runCount(filters: $filters)
      tempSelectedCount: runCount(filters: $tempFilters)
      tempSelectedActiveCount: runActiveCount(filters: $tempFilters)
      tempOutputArtifactsCount: runOutputArtifactsCount(filters: $tempFilters)
    }
  }
`;

// These types define the inputs and outputs of the above gql query

export interface SelectedCountData {
  id: string;
  selectedCount: number;
  tempOutputArtifactsCount: number;
  tempSelectedCount: number;
  tempSelectedActiveCount: number;
}

// The query's output shape.
interface Data {
  project: SelectedCountData;
}

interface QueryInputProps {
  mergeFilters?: FilterTypes.Filter<RunTypes.Key>;
  spamFilters?: FilterTypes.Filter<RunTypes.Key>;
  filters: FilterTypes.Filter<RunTypes.Key>;
  groupSelection: SM.GroupSelectionState;
  groupTempSelection?: SM.GroupSelectionState;
  search?: RunSetConfig['search'];
  customRunNames?: CustomRunNamesTypes.CustomRunNames;
  projectInternalId: string | undefined;
}

export function useSelectedCountQuery(
  inputProps: QueryInputProps
): ReturnType<typeof useSelectedCountQueryNoCache> {
  const selectedCountQuery = useSelectedCountQueryNoCache(inputProps);

  const initialLoading =
    selectedCountQuery.loading && selectedCountQuery.data == null;

  const selectedCountFromServerOrCache = useCachedData({
    namespace: 'SelectedCountQuery',
    key: inputProps,
    dataFromServer: selectedCountQuery.data,
    waitingForDataFromServer: initialLoading,
  });

  return useMemo(() => {
    if (initialLoading && selectedCountFromServerOrCache != null) {
      return {
        ...selectedCountQuery,
        data: selectedCountFromServerOrCache,
        loading: false,
      };
    }
    return selectedCountQuery;
  }, [initialLoading, selectedCountFromServerOrCache, selectedCountQuery]);
}

function useSelectedCountQueryNoCache(inputProps: QueryInputProps) {
  return Generated.useSelectedCountQuery({
    variables: {
      projectInternalId: inputProps.projectInternalId ?? '',
      filters: JSON.stringify(
        combinedFilter(
          inputProps.filters,
          inputProps.groupSelection,
          inputProps.spamFilters,
          inputProps.mergeFilters,
          inputProps.search,
          inputProps.customRunNames
        )
      ),
      tempFilters: JSON.stringify(
        combinedFilter(
          inputProps.filters,
          inputProps.groupTempSelection,
          undefined,
          inputProps.mergeFilters,
          inputProps.search,
          inputProps.customRunNames
        )
      ),
    },
    fetchPolicy: 'network-only',
    skip: inputProps.projectInternalId == null,
  });
}

// A more component-friendly version of useSelectedCountQuery. Returns the most recent counts.
export function useSelectedCounts(
  runSetRef: RunSetTypes.Ref,
  tempSelectionsRef?: TempSelectionsTypes.Ref,
  customRunNamesRef?: CustomRunNamesTypes.Ref,
  refetch?: boolean
) {
  const customRunNames = ViewHooks.usePartMaybe(customRunNamesRef);
  const tempSelections = ViewHooks.usePartMaybe(tempSelectionsRef);
  const runSetPart = ViewHooks.usePart(runSetRef);
  const groupSelections = ViewHooks.usePart(runSetPart.groupSelectionsRef);
  const filters = ViewHooks.usePart(runSetPart.filtersRef);

  const runsQueryContext = useRunsQueryContext();
  const {projectInternalId} = useRunSetProject(runSetPart.project);
  const groupTempSelection = tempSelections
    ? {
        grouping: groupSelections.grouping,
        selections: tempSelections,
        expandedRowAddresses: groupSelections.expandedRowAddresses,
      }
    : undefined;
  const selectedCountQueryProps = {
    ...runsQueryContext,
    projectInternalId,
    filters,
    groupSelection: groupSelections,
    groupTempSelection,
    search: runSetPart.search,
    customRunNames,
  };
  const selectedCountQuery = useSelectedCountQuery(selectedCountQueryProps);
  if (refetch && 'refetch' in selectedCountQuery) {
    selectedCountQuery.refetch();
  }

  const selectedCountsCached = useRef({
    tempOutputArtifactsCount: 0,
    tempSelectedCount: 0,
    tempSelectedActiveCount: 0,
    selectedCount: 0,
  });

  if (!selectedCountQuery.loading && selectedCountQuery.data?.project != null) {
    const project = selectedCountQuery.data?.project;
    selectedCountsCached.current.tempOutputArtifactsCount =
      project.tempOutputArtifactsCount;
    selectedCountsCached.current.tempSelectedCount = project.tempSelectedCount;
    selectedCountsCached.current.selectedCount = project.selectedCount;
    selectedCountsCached.current.tempSelectedActiveCount =
      project.tempSelectedActiveCount;
  }

  return selectedCountsCached.current;
}
